/**
 * Phase 6 — Redesigned Button
 *
 * Clean, Linear-grade. Five variants only (vs. legacy's eleven).
 * Use the legacy <Button> from @/components/ui/button for un-migrated pages.
 * Migrate page-by-page; the two coexist.
 */

import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";

const buttonVariants = cva(
  [
    "inline-flex items-center justify-center gap-1.5 whitespace-nowrap select-none",
    "rounded-[8px] font-medium",
    "transition-[background,border-color,color,box-shadow,transform] duration-[120ms] ease-[cubic-bezier(0.16,1,0.3,1)]",
    "focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50",
    "[&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg]:size-[14px]",
    "active:translate-y-[0.5px]",
  ].join(" "),
  {
    variants: {
      variant: {
        primary: [
          "bg-[hsl(var(--rd-accent-500))] text-white",
          "hover:bg-[hsl(var(--rd-accent-600))]",
          "active:bg-[hsl(var(--rd-accent-700))]",
          "shadow-[inset_0_1px_0_0_rgb(255_255_255/0.10)]",
        ].join(" "),
        secondary: [
          "bg-[hsl(var(--rd-surface-2))] text-[hsl(var(--rd-text))]",
          "border border-[hsl(var(--rd-border))]",
          "hover:bg-[hsl(var(--rd-surface-3))] hover:border-[hsl(var(--rd-border-strong))]",
        ].join(" "),
        ghost: [
          "bg-transparent text-[hsl(var(--rd-text))]",
          "hover:bg-[hsl(var(--rd-surface-3))]",
        ].join(" "),
        outline: [
          "bg-transparent text-[hsl(var(--rd-text))]",
          "border border-[hsl(var(--rd-border))]",
          "hover:bg-[hsl(var(--rd-surface-2))] hover:border-[hsl(var(--rd-border-strong))]",
        ].join(" "),
        destructive: [
          "bg-[hsl(var(--rd-error))] text-white",
          "hover:opacity-90",
        ].join(" "),
        link: [
          "bg-transparent text-[hsl(var(--rd-accent-400))] underline-offset-4",
          "hover:underline px-0",
        ].join(" "),
      },
      size: {
        sm: "h-7  px-2.5 text-[12px]",
        md: "h-8  px-3   text-[13px]",
        lg: "h-10 px-4   text-[14px]",
        icon: "h-8 w-8 p-0",
      },
    },
    defaultVariants: {
      variant: "primary",
      size: "md",
    },
  }
);

export interface RdButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

export const RdButton = React.forwardRef<HTMLButtonElement, RdButtonProps>(
  ({ className, variant, size, asChild, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        ref={ref}
        className={cn(buttonVariants({ variant, size }), className)}
        {...props}
      />
    );
  }
);
RdButton.displayName = "RdButton";

export { buttonVariants as rdButtonVariants };
